import React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { Bot, User, Check, Clock, Quote } from "lucide-react";
import TypingAnimation from "./TypingAnimation";
import AudioPlayer from "./AudioPlayer";
import INPUT_TYPES from "../constants/input-types";
import SpeechSynthesis from "./SpeechSynthesis";
import MESSAGE_SENDER_TYPES from "../constants/message-sender-types";
import { extractPlainText, MarkdownRenderer, containsQuotedMarkdown } from "../utils/markdownRenderer";
import ImageTableIcons from "./ImgAndTableButtons";
import ThumsUpDown from "./ThumsUpDown";
import PDFGenerator from "./PDFGenerator";
import DebugInfoButton from "./DebugInfoButton";
import { getMessageActionClassName, MESSAGE_ACTION_STYLES } from "../utils/messageActionStyles";
import { 
  FOLLOW_UP_INTENTS, 
  FOLLOW_UP_INTENT_LABELS,
  FOLLOW_UP_INTENT_DESCRIPTIONS,
  FOLLOW_UP_UI_TEXT
} from "../constants/follow-up-intents";

// Helper functions to reduce cognitive complexity
const getAvatarClasses = (isUser) => {
  return isUser
    ? "bg-blue-100 dark:bg-blue-900"
    : "bg-gray-100 dark:bg-gray-700";
};

const getBubbleClasses = (isUser, isSelected) => {
  const baseClasses = "relative p-4 rounded-2xl shadow-sm transition-colors";
  const userClasses = "bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 text-white rounded-br-none";
  const botClasses = "bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-none";
  const borderClasses = isSelected ? "border-2 border-blue-500" : "border-2 border-transparent";
  
  return `${baseClasses} ${isUser ? userClasses : botClasses} ${borderClasses}`;
};

const getSenderLabelClasses = (isUser) => {
  return isUser
    ? "text-right text-blue-500 dark:text-blue-400"
    : "text-left text-gray-500 dark:text-gray-400";
};

const getTimeClasses = (isUser) => {
  return isUser ? "text-blue-100" : "text-gray-500 dark:text-gray-400";
};

// Helper functions to check message types
const isQuotedMessage = (message) => {
  return typeof message === "object" && 
         message?.isQuoted && 
         message?.quotedContent && 
         message?.additionalText;
};

const isStructuredMessage = (message) => {
  return typeof message === "object" && 
         message && 
         !message.isQuoted && 
         message.additionalText;
};

const isTextMessage = (messageType) => {
  return messageType?.toLowerCase().trim() === INPUT_TYPES.TEXT;
};

// Extract quoted message content component
const QuotedMessageContent = ({ message }) => {
  const quotedText = extractPlainText(message.quotedContent);
  const truncatedQuotedText = quotedText.length > 80 
    ? `${quotedText.substring(0, 80)}...` 
    : quotedText;
  
  return (
    <div className="space-y-3">
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border-l-4 border-blue-500">
        <div className="flex items-start gap-2 mb-2">
          <Quote size={14} className="text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Quoted message</span>
        </div>
        <div className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
          {truncatedQuotedText}
        </div>
      </div>
      <div className="pl-2">
        <p className="whitespace-pre-wrap">{message.additionalText}</p>
      </div>
    </div>
  );
};

QuotedMessageContent.propTypes = {
  message: PropTypes.shape({
    isQuoted: PropTypes.bool,
    quotedContent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    additionalText: PropTypes.string.isRequired,
  }).isRequired,
};

// Extract message content rendering logic
const renderMessageContent = (data, isUser) => {
  if (!isUser) {
    return <TypingAnimation text={data.message} />;
  }

  if (!isTextMessage(data.message_type)) {
    return <AudioPlayer file={data.message} />;
  }

  const message = data.message;
  
  if (isQuotedMessage(message)) {
    return <QuotedMessageContent message={message} />;
  }

  if (isStructuredMessage(message)) {
    return <p className="whitespace-pre-wrap">{message.additionalText}</p>;
  }

  if (typeof message === "string") {
    if (containsQuotedMarkdown(message)) {
      return <MarkdownRenderer content={message} />;
    }
    return <p className="whitespace-pre-wrap">{message}</p>;
  }

  const fallbackText = message?.additionalText || "Message format not supported";
  return (
    <p className="whitespace-pre-wrap text-gray-600 dark:text-gray-400">
      {fallbackText}
    </p>
  );
};

// Helper function to get button classes
const getButtonClasses = (isProcessing, isActive, activeClasses, inactiveClasses) => {
  if (isProcessing) {
    return 'bg-gray-400 text-gray-200 cursor-not-allowed';
  }
  return isActive ? activeClasses : inactiveClasses;
};

// Extract follow-up intent button component
const FollowUpIntentButton = ({ 
  intent, 
  label, 
  isActive, 
  isProcessing, 
  onClick, 
  activeClasses, 
  inactiveClasses 
}) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isProcessing) return;
    onClick?.();
  };

  const buttonClasses = getButtonClasses(isProcessing, isActive, activeClasses, inactiveClasses);
  const buttonText = isProcessing ? FOLLOW_UP_UI_TEXT.PROCESSING : label;

  return (
    <button
      onClick={handleClick}
      disabled={isProcessing}
      className={`px-4 py-2 text-xs rounded-md transition-colors active:scale-95 flex-shrink-0 w-full sm:w-auto text-center ${buttonClasses}`}
      style={{ pointerEvents: isProcessing ? 'none' : 'auto' }}
    >
      {buttonText}
    </button>
  );
};

FollowUpIntentButton.propTypes = {
  intent: PropTypes.string,
  label: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  isProcessing: PropTypes.bool,
  onClick: PropTypes.func,
  activeClasses: PropTypes.string.isRequired,
  inactiveClasses: PropTypes.string.isRequired,
};

// Helper function to check if choice section should be shown
const shouldShowChoiceSection = (showChoice, isUser, followUpIntentExpressed) => {
  return showChoice && !isUser && !followUpIntentExpressed;
};

// Helper function to get waiting message text
const getWaitingMessageText = (message) => {
  const cleanedMessage = message?.replace(FOLLOW_UP_UI_TEXT.PLEASE_PROVIDE_PREFIX, '') || FOLLOW_UP_UI_TEXT.MISSING_INFO;
  return `${FOLLOW_UP_UI_TEXT.WAITING_FOR} ${cleanedMessage}`;
};

// Extract follow-up choice section
const FollowUpChoiceSection = ({
  data,
  followUpIntentExpressed,
  userFollowUpIntent,
  isProcessingChoice,
  onProvideIntent,
  onAskSomethingElse,
  isUser,
}) => {
  if (!shouldShowChoiceSection(data.showChoice, isUser, followUpIntentExpressed)) {
    return null;
  }

  const messageText = getWaitingMessageText(data.message);

  return (
    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 w-full max-w-full overflow-hidden">
      <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
        {messageText}
      </p>
      <div className="flex flex-wrap gap-2 sm:gap-3 items-stretch">
        <FollowUpIntentButton
          intent={FOLLOW_UP_INTENTS.PROVIDE}
          label={FOLLOW_UP_INTENT_LABELS[FOLLOW_UP_INTENTS.PROVIDE]}
          isActive={userFollowUpIntent === FOLLOW_UP_INTENTS.PROVIDE}
          isProcessing={isProcessingChoice}
          onClick={onProvideIntent}
          activeClasses="bg-blue-600 text-white border-2 border-blue-700 cursor-pointer"
          inactiveClasses="bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
        />
        <FollowUpIntentButton
          intent={FOLLOW_UP_INTENTS.ASK_ELSE}
          label={FOLLOW_UP_INTENT_LABELS[FOLLOW_UP_INTENTS.ASK_ELSE]}
          isActive={userFollowUpIntent === FOLLOW_UP_INTENTS.ASK_ELSE}
          isProcessing={isProcessingChoice}
          onClick={onAskSomethingElse}
          activeClasses="bg-green-600 text-white border-2 border-green-700 cursor-pointer"
          inactiveClasses="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
        />
      </div>
    </div>
  );
};

FollowUpChoiceSection.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showChoice: PropTypes.bool,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  followUpIntentExpressed: PropTypes.bool,
  userFollowUpIntent: PropTypes.string,
  isProcessingChoice: PropTypes.bool,
  onProvideIntent: PropTypes.func,
  onAskSomethingElse: PropTypes.func,
  isUser: PropTypes.bool.isRequired,
};

// Helper function to check if confirmation should be shown
const shouldShowConfirmation = (showChoice, isUser, followUpIntentExpressed) => {
  return showChoice && !isUser && followUpIntentExpressed;
};

// Extract follow-up intent confirmation
const FollowUpIntentConfirmation = ({ showChoice, isUser, followUpIntentExpressed, userFollowUpIntent }) => {
  if (!shouldShowConfirmation(showChoice, isUser, followUpIntentExpressed)) {
    return null;
  }

  const intentLabel = FOLLOW_UP_INTENT_LABELS[userFollowUpIntent] || FOLLOW_UP_UI_TEXT.UNKNOWN_INTENT;

  return (
    <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
      <p className="text-xs text-green-700 dark:text-green-300">
        ✓ {intentLabel}
      </p>
    </div>
  );
};

FollowUpIntentConfirmation.propTypes = {
  showChoice: PropTypes.bool,
  isUser: PropTypes.bool.isRequired,
  followUpIntentExpressed: PropTypes.bool,
  userFollowUpIntent: PropTypes.string,
};

// Helper function to get container direction
const getContainerDirection = (isUser) => {
  return isUser ? "flex-row-reverse" : "flex-row";
};

// Helper function to get justify classes
const getJustifyClasses = (isUser) => {
  return isUser ? "justify-end" : "justify-start";
};

// Extract avatar icon component
const AvatarIcon = ({ isUser }) => {
  if (isUser) {
    return <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
  }
  return <Bot className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
};

AvatarIcon.propTypes = {
  isUser: PropTypes.bool.isRequired,
};

// Extract status icon component
const StatusIcon = ({ status }) => {
  if (status === "sending") {
    return <Clock className="w-3 h-3" />;
  }
  return <Check className="w-3 h-3" />;
};

StatusIcon.propTypes = {
  status: PropTypes.string,
};

// Extract bot message actions component
const BotMessageActions = ({ data, onSelect, imageId, tableId, dialogId, onQuote, quotedMessageId, time }) => {
  return (
    <div className="flex items-start gap-2">
      <SpeechSynthesis
        displayText={extractPlainText(data.message)}
        onSelect={onSelect}
        messageId={data.id}
      />
      <ImageTableIcons imageId={imageId} tableId={tableId} />
      <ThumsUpDown dialogId={dialogId} />
      <PDFGenerator 
        message={data.message}
        messageId={data.id}
        timestamp={time}
      />
      <DebugInfoButton 
        debugInfo={data.debug_info}
        messageId={data.id}
      />
      <button
        onClick={() => onQuote && onQuote(data)}
        className={getMessageActionClassName('quote', quotedMessageId === data.id, false)}
        title="Quote this message"
      >
        <Quote size={MESSAGE_ACTION_STYLES.ICON_SIZE} />
      </button>
    </div>
  );
};

BotMessageActions.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    debug_info: PropTypes.object,
  }).isRequired,
  onSelect: PropTypes.func,
  imageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dialogId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onQuote: PropTypes.func,
  quotedMessageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  time: PropTypes.string,
};

const ChatBubble = ({
  data,
  selectedMessageId,
  onSelect,
  imageId,
  tableId,
  dialogId,
  onQuote,
  quotedMessageId,
  onProvideIntent,
  onAskSomethingElse,
  followUpIntentExpressed,
  userFollowUpIntent,
  isProcessingChoice,
}) => {
  const isUser = data.sender === MESSAGE_SENDER_TYPES.USER;
  const isSelected = selectedMessageId === data.id;

  const time = data.timestamp || new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const containerClasses = `group flex items-end mt-4 ${getContainerDirection(isUser)}`;
  const avatarClasses = `h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${getAvatarClasses(isUser)} transition-colors`;
  const senderLabelClasses = `text-xs ${getSenderLabelClasses(isUser)} transition-colors`;
  const justifyClasses = `flex items-center gap-2 mb-1 ${getJustifyClasses(isUser)}`;
  const timeClasses = `flex items-center gap-1 mt-1 text-xs opacity-70 ${getTimeClasses(isUser)} transition-colors`;

  return (
    <>
      <div className={containerClasses} data-id={data.id}>
        <div className={avatarClasses}>
          <AvatarIcon isUser={isUser} />
        </div>

        <div className="max-w-[80%] flex flex-col msgBubble">
          <div className={justifyClasses}>
            <span className={senderLabelClasses}>
              {isUser ? "You" : ""}
            </span>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
            className={getBubbleClasses(isUser, isSelected)}
          >
            <div className="text-sm font-sm break-words">
              {renderMessageContent(data, isUser)}
            </div>
            
            <FollowUpChoiceSection
              data={data}
              followUpIntentExpressed={followUpIntentExpressed}
              userFollowUpIntent={userFollowUpIntent}
              isProcessingChoice={isProcessingChoice}
              onProvideIntent={onProvideIntent}
              onAskSomethingElse={onAskSomethingElse}
              isUser={isUser}
            />
            <FollowUpIntentConfirmation
              showChoice={data.showChoice}
              isUser={isUser}
              followUpIntentExpressed={followUpIntentExpressed}
              userFollowUpIntent={userFollowUpIntent}
            />
            
            <div className={timeClasses}>
              <span>{time}</span>
              {isUser && (
                <div className="flex items-center gap-0.5">
                  <StatusIcon status={data.status} />
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
      <span className="text-xs ml-10 text-left text-gray-500 dark:text-gray-400 transition-colors">
        {!isUser && (
          <BotMessageActions
            data={data}
            onSelect={onSelect}
            imageId={imageId}
            tableId={tableId}
            dialogId={dialogId}
            onQuote={onQuote}
            quotedMessageId={quotedMessageId}
            time={time}
          />
        )}
      </span>
    </>
  );
};

ChatBubble.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sender: PropTypes.string.isRequired,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    message_type: PropTypes.string,
    timestamp: PropTypes.string,
    status: PropTypes.string,
    showChoice: PropTypes.bool,
    debug_info: PropTypes.object,
  }).isRequired,
  selectedMessageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func,
  imageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dialogId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onQuote: PropTypes.func,
  quotedMessageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onProvideIntent: PropTypes.func,
  onAskSomethingElse: PropTypes.func,
  followUpIntentExpressed: PropTypes.bool,
  userFollowUpIntent: PropTypes.string,
  isProcessingChoice: PropTypes.bool,
};

export default ChatBubble;
